import type * as t from "./types";

import { createAppContext } from "./quiz-store";
import { Sequence } from "./sequence";
import { createSegments, displayConfigError } from "./utils";
import { get } from "svelte/store";

/**
 * All this function does is convert empty strings to undefined. Because of how
 * props are passed to the quiz, optional props that are not present come in as
 * empty strings.
 * */
function parsePropValue(propValue: string | number) {
    if (typeof propValue !== "string") {
        return propValue;
    }
    if (propValue.length === 0) {
        return;
    }
    return propValue;
}

function getBlockSetting<T = string | number>(
    settings: t.Block["settings"],
    key: string,
) {
    if (!(key in settings)) {
        return;
    }
    return parsePropValue(settings[key]) as T;
}

function createSegmentResultStep(
    block: t.SegmentResultBlock,
): t.SegmentResultStep {
    const subtitleText = getBlockSetting<string>(block.settings, "subtitle");
    let subtitle: t.SegmentResultStep["subtitle"];
    if (subtitleText != null) {
        subtitle = {
            text: subtitleText,
            position: block.settings.subtitle_position,
        };
    }

    const image = getBlockSetting<t.Image>(block.settings, "image");

    return {
        type: "segment_result",
        segmentId: block.settings.segment_id,
        title: block.settings.title,
        subtitle,
        image,
        continueButton: {
            text: block.settings.continue_button_text,
        },
        defaultFormInputType: block.settings.default_form_input_type,
        state: {
            formStatus: "hidden",
        },
    };
}

function validateEducationalStep(block: t.Block) {
    if (block.settings.video != null && block.settings.image != null) {
        displayConfigError(
            "Error: Educational step cannot have both video and image",
        );
    }
}

function createEducationalStep(
    block: t.Block<t.EducationalSettings>,
): t.EducationalStep {
    validateEducationalStep(block);

    const video = getBlockSetting<t.Shopify.Video>(block.settings, "video");
    const image = getBlockSetting<t.Image>(block.settings, "image");

    return {
        type: "educational",
        title: block.settings.title,
        text: block.settings.text,
        waitUntilCanProceed: block.settings.wait_until_can_proceed,
        video,
        image,
        order: block.settings.order,
        state: {
            canProceed: false,
        },
    };
}

function createMultipleChoiceStep(block: t.Block): t.MultipleChoiceStep {
    const optionType = block.settings.option_type;
    const options: t.MultipleChoiceOption[] = [];
    for (let i = 1; i <= 4; i++) {
        const titleKey = `option_${i}_title`;
        const backgroundImageSrcKey = `option_${i}_background_image`;
        const iconSrcKey = `option_${i}_icon`;
        const answerIdKey = `option_${i}_answer_id`;

        const title = getBlockSetting<string>(block.settings, titleKey);
        if (title == null) {
            continue;
        }

        /**
         * answerId being zero means that its value was manually cleared out
         * in the Shopify theme editor.
         * */
        let answerId = getBlockSetting<number>(block.settings, answerIdKey);
        if (answerId === 0) {
            answerId = undefined;
        }

        const backgroundImage = getBlockSetting<t.Image>(
            block.settings,
            backgroundImageSrcKey,
        );
        const icon = getBlockSetting<t.Image>(block.settings, iconSrcKey);

        options.push({
            title,
            backgroundImage,
            icon,
            answerId,
        });
    }

    const subtitle = getBlockSetting<string>(block.settings, "subtitle");
    const bgImgObjFit =
        getBlockSetting<t.MultipleChoiceStep["backgroundImageObjectFit"]>(
            block.settings,
            "bg_image_object_fit",
        ) ?? "cover";

    return {
        type: "multiple_choice",
        title: block.settings.title,
        subtitle,
        optionType,
        backgroundImageObjectFit: bgImgObjFit,
        options,
        state: {
            selectedOption: undefined,
        },
    };
}

function createMinigameStep(block: t.Block): t.MinigameStep {
    const id = getBlockSetting<string>(block.settings, "id");
    return {
        type: "minigame",
        id,
        minigame: block.settings.type,
        state: {
            gameStatus: "initial",
        },
    };
}

function createConstructMinigameStep(block: t.Block): t.ConstructMinigameStep {
    const iframeSrc = getBlockSetting<string>(block.settings, "iframe_src");
    // if (iframeSrc == null || iframeSrc.length === 0) {
    //     const msg = `[shpln-quiz] No iframe src was provided for Construct minigame block: ${JSON.stringify(block, null, 2)}`;
    //     throw new Error(msg);
    // }

    const title = getBlockSetting<string>(block.settings, "title");
    // if (title == null || title.length === 0) {
    //     const msg = `[shpln-quiz] No title src was provided for Construct minigame block: ${JSON.stringify(block, null, 2)}`;
    //     throw new Error(msg);
    // }

    const subtitle = getBlockSetting<string>(block.settings, "subtitle");
    const image = getBlockSetting<t.Image>(block.settings, "image");
    const buttonText = getBlockSetting<string>(block.settings, "button_text");
    const imageCaption = getBlockSetting<string>(
        block.settings,
        "image_caption",
    );
    const imageObjectFit = getBlockSetting<t.ObjectFit>(
        block.settings,
        "image_object_fit",
    );
    if (image != null && imageObjectFit == null) {
        const msg = `[shpln-quiz] image_object_fit is required when image is provided for Construct minigame block: ${JSON.stringify(block, null, 2)}`;
        throw new Error(msg);
    }

    let config: t.ConstructMinigameConfig;
    try {
        const configJson = getBlockSetting<string>(block.settings, "config");
        if (configJson == null) {
            throw new Error("Missing Construct Minigame Step config.");
        }
        config = JSON.parse(configJson);
    } catch (err) {
        throw new Error(
            `[shpln-quiz] Failed to parse Construct Minigame step config: ${err}`,
        );
    }

    return {
        type: "construct_minigame",
        iframeSrc,
        title,
        subtitle,
        buttonText: buttonText ?? "Start",
        image,
        imageCaption,
        imageObjectFit: imageObjectFit ?? "contain",
        config,
        state: {
            gameStatus: "initial",
        },
    };
}

function createNetworkPortalStep(block: t.Block): t.NetworkPortalStep {
    return {
        type: "network_portal",
    };
}

function createSteps(blocks: t.ShopifyProps["blocks"]) {
    const steps: t.Step[] = [];
    const mainBlocks = blocks.filter((b) => b.type !== "intro");

    for (const block of mainBlocks) {
        let step: t.Step | undefined;

        switch (block.type) {
            case "multiple_choice":
                step = createMultipleChoiceStep(block);
                break;
            case "educational":
                step = createEducationalStep(block);
                break;
            case "segment_result":
                step = createSegmentResultStep(block);
                break;
            case "minigame":
                step = createMinigameStep(block);
                break;
            case "construct_minigame":
                step = createConstructMinigameStep(block);
                break;
            case "network_portal":
                step = createNetworkPortalStep(block);
                break;
            default:
                throw new Error(`Unrecognized block type: ${block.type}`);
        }

        if (step == null) {
            console.error(
                "[shpln-quiz] Invalid step was created from block: ",
                block,
            );
            throw new Error("Unexpectedly got a null step");
        }

        steps.push(step);
    }

    return steps;
}

function validateProps(props: t.ShopifyProps) {
    switch (true) {
        case props.config == null || Object.keys(props.config).length === 0:
            console.error(
                "[shpln-quiz] Config is missing or incomplete: ",
                props.config,
            );
    }
}

export function initCtx(props: t.ShopifyProps) {
    validateProps(props);

    const introBlock = props.blocks.find((b) => b.type === "intro");
    if (introBlock == null) {
        throw new Error("[shpln-quiz]: Intro block was not found");
    }

    const steps = createSteps(props.blocks);
    const segments = createSegments(props.config, steps);
    const config: t.QuizConfig = {
        ...props.config,
        privacyPolicyUrl: props.privacyPolicyUrl,
    };

    const segmentsSeq = new Sequence<t.Segment>(segments);
    const totalSteps = segments.reduce((acc, segment) => {
        return acc + segment.steps.length;
    }, 0);

    const ctx = createAppContext({
        config,
        segments: segmentsSeq,
        step: segmentsSeq.head!.value.steps.head!,
        segment: segmentsSeq.head!,
        globalStepIdx: -1,
        score: {},
        totalSteps,
        emailMarketingConfig: props.emailMarketingConfig,
        shop: props.shop,
        status: "initial",
        instanceId: props.instanceId,
        presentationStyle: props.presentationStyle,
        minigameResult: undefined,
    });
    const { state } = ctx;
    if (
        window.shpln_quiz != null &&
        window.shpln_quiz[props.instanceId] != null
    ) {
        window.shpln_quiz[props.instanceId].state = get(state);
    }

    return ctx;
}
